import {
  StyledActiveTriggerLogoWrapper,
  StyledIntegrationLabel,
  StyledTriggerLabel,
  StyledButtonOpenTriggerActionMenu,
} from "../style";
import {
  StyledAutomationHeader,
  StyledAutomationHeaderActions,
  StyledAutomationHeaderBody,
  StyledAutomationHeaderText,
  StyledAutomationHeaderTitle,
} from "../AutomationHeader/style";
import type { MutableRefObject, ReactElement } from "react";
import { useEffect, useRef, useState } from "react";
import type {
  ActionDescription,
  Info,
  IntegrationDescription,
} from "@smartsuite/automation-engine-api";
import { useRootStore } from "../../../../stores/hooks";
import type { ActionCandidate } from "../../../../types/candidate";
import { observer } from "mobx-react-lite";
import { translateAction, translateIfPresent } from "../../../../locales/i18n";
import { getIntegrationLogo } from "../../../../utils/integration-logo";
import * as R from "remeda";
import { ActionMenu } from "../../../../components/ActionMenu";
import { InfoSection } from "../../../../components/InfoSection";
import { StyledBack } from "./style";
import { useTranslation } from "react-i18next";
import { ActionDescription_Testable } from "@smartsuite/automation-engine-api";
import type { Weight } from "@smartsuite/react-ui/lib";
import {
  Icon,
  Tooltip,
  Text,
  Button,
  TextSizes,
} from "@smartsuite/react-ui/lib";
import { GrayColors, TextColorNames } from "@smartsuite/types";

const mergeDescriptionInfo = (
  selectedAction: ActionDescription | undefined,
  info: Info | undefined,
): ActionDescription | undefined => {
  if (info === undefined || selectedAction === undefined) {
    return selectedAction;
  }

  if (info.details === undefined) {
    return selectedAction;
  }

  return {
    ...selectedAction,
    info,
  };
};

export const ActionSelectorMenu = observer<{
  action: ActionCandidate;
  isOpen: boolean;
  onClick: () => void;
  onClickOutside: () => void;
  onChangeAction: (integrationId: string, actionId: string) => void;
  onClickTest: (path?: string) => void;
  path?: string;
}>(
  ({
    action,
    isOpen,
    onChangeAction,
    onClick,
    onClickOutside,
    onClickTest,
    path,
  }) => {
    const { t } = useTranslation();
    const changeActionRef = useRef<HTMLDivElement>(null);
    const [selectedIntegration, setSelectedIntegration] =
      useState<IntegrationDescription>();
    const store = useRootStore();
    const integrations = store.integrations.list;
    const [selectedAction, setSelectedAction] = useState<ActionDescription>();
    const [open, setOpen] = useState<boolean>(false);

    useEffect(() => {
      setOpen(isOpen);
    }, [isOpen]);

    useEffect(() => {
      setSelectedIntegration(
        integrations.find(
          (integration) =>
            integration.integrationId ===
            action?.actionReference?.integrationId,
        ),
      );
    }, [action, integrations]);

    useEffect(() => {
      setSelectedAction(
        mergeDescriptionInfo(
          selectedIntegration?.actions.find(
            (item) => item.actionId === action?.actionReference?.actionId,
          ),
          action.description.info,
        ),
      );
    }, [selectedIntegration, action]);

    const IntegrationLogo = getIntegrationLogo(
      String(selectedIntegration?.integrationId),
    );

    const renderTitle = (
      selectedIntegration: IntegrationDescription,
      selectedAction: ActionDescription,
      path?: string,
    ): ReactElement => {
      if (path) {
        return (
          <StyledTriggerLabel>
            {t("general.test")}: {translateAction(selectedAction)}
          </StyledTriggerLabel>
        );
      }

      return (
        <>
          <StyledIntegrationLabel>
            {translateAction(selectedIntegration)}:&nbsp;
          </StyledIntegrationLabel>
          <StyledTriggerLabel>
            {translateAction(selectedAction)}
          </StyledTriggerLabel>
        </>
      );
    };

    return (
      <>
        <StyledAutomationHeader as="section">
          <StyledActiveTriggerLogoWrapper>
            {path ? (
              <Tooltip
                padding={2}
                text={""}
                placements={["top", "bottom"]}
                target={
                  <StyledBack
                    onClick={() =>
                      onClickTest(path === "test-result" ? "test" : undefined)
                    }
                  >
                    <Icon
                      name="arrow-left-1"
                      color="currentColor"
                      transition={true}
                    />
                  </StyledBack>
                }
              />
            ) : (
              <IntegrationLogo />
            )}
          </StyledActiveTriggerLogoWrapper>
          <StyledAutomationHeaderBody>
            <StyledAutomationHeaderText>
              <StyledAutomationHeaderTitle>
                {R.isTruthy(selectedIntegration) && R.isTruthy(selectedAction)
                  ? renderTitle(selectedIntegration, selectedAction, path)
                  : t("automation-form.action-selector")}
              </StyledAutomationHeaderTitle>
              {R.isTruthy(selectedAction) &&
                R.isTruthy(selectedAction.subLabel) && (
                  <Text size="m" color="secondary">
                    {translateIfPresent(
                      `actions.${selectedAction.subLabel}`,
                      selectedAction.subLabel,
                    )}
                  </Text>
                )}
            </StyledAutomationHeaderText>
            {!path && (
              <StyledAutomationHeaderActions>
                {R.isTruthy(selectedIntegration) &&
                  R.isTruthy(selectedAction) && (
                    <StyledButtonOpenTriggerActionMenu
                      ref={changeActionRef}
                      onClick={onClick}
                    >
                      {t("general.change-action")}
                    </StyledButtonOpenTriggerActionMenu>
                  )}
                {action.description.testable ===
                  ActionDescription_Testable.ACTION_TEST && (
                  <Button
                    onClick={() => onClickTest("test")}
                    styles={{
                      background: GrayColors["G-0"],
                      textColor: TextColorNames.primary,
                      height: 28,
                      sidePadding: 15,
                      textSize: TextSizes.XS,
                      textWeight: "semibold" as Weight,
                      borderRadius: 5,
                      borderWidth: 1,
                      borderColor: GrayColors["G-1-5"],
                      borderHoverColor: GrayColors["G-2"],
                    }}
                  >
                    {translateIfPresent(
                      "actions.test-action-step",
                      "Test Step",
                    )}
                  </Button>
                )}
              </StyledAutomationHeaderActions>
            )}
          </StyledAutomationHeaderBody>
          <ActionMenu
            dropdownMaxWidth={300}
            dropdownMinWidth={300}
            // ActionMenu no longer gates Conditional Logic on
            // hasBranches internally (Nested Advanced Logic, Task 3) --
            // this consumer passes no showAdvancedLogic (defaults to
            // true), so preserve its pre-existing behavior (CL hidden
            // once a branches block exists anywhere) explicitly.
            advancedLogicItems={{
              branches: !store.automations.candidate.branches.hasBranches,
            }}
            menuButtonRef={changeActionRef as MutableRefObject<HTMLElement>}
            isMenuOpen={open}
            setMenuOpen={setOpen}
            onActionChange={(integrationId, actionId) => {
              setOpen(false);
              if (actionId === action?.actionReference?.actionId) {
                return;
              }
              store.modal.openConfirmModal({
                type: "warning",
                title: t("confirm-modal.are-you-sure"),
                description: `${t("confirm-modal.want-change-action")} ${t(
                  "confirm-modal.this-may-invalidate-action",
                )}. ${t("confirm-modal.cannot-undone-action")}.`,
                positiveButtonText: t("confirm-modal.yes-change-action"),
                negativeButtonText: t("confirm-modal.no-cancel"),
                onClose: () => {
                  store.modal.closeConfirmModal();
                },
                onConfirm: () => {
                  onChangeAction(integrationId, actionId);
                  store.modal.closeConfirmModal();
                },
              });
            }}
          />
        </StyledAutomationHeader>

        {R.isTruthy(selectedAction) &&
          R.isTruthy(selectedAction.info?.details?.title) && (
            <InfoSection info={selectedAction.info} />
          )}
      </>
    );
  },
);
